import { ProductId } from "@settings/products";

import { Quote } from "@organisms/result/domain/result";

import { Product } from "@features/quotes-funnel/domain/product";
import { Coupon, PromotionCodeType } from "@features/quotes-funnel/domain/promotion-code";
import { PaymentIntervalMonths } from "@features/quotes-funnel/settings/payments";

export interface ShoppingCartDTO {
  products: Product[];
  paymentIntervalMonths: number;
  coupon: Coupon | undefined;
  currency: string;
}

export class ShoppingCart {
  private static readonly MONTHS_IN_YEAR = 12;

  private readonly _products: Product[];
  private readonly _paymentIntervalMonths: number;
  private readonly _coupon: Coupon | undefined;
  private readonly _currency: string;

  constructor(shoppingCart: ShoppingCartDTO) {
    this._products = shoppingCart.products;
    this._paymentIntervalMonths = shoppingCart.paymentIntervalMonths;
    this._coupon = shoppingCart.coupon;
    this._currency = shoppingCart.currency;
  }

  static isProductSelectedByDefault = (quotes: Quote[], productId: ProductId): boolean => {
    const { healthBasic, healthUnlimited } = this.getHealthProductsSelectedStatus(quotes);
    switch (productId) {
      case ProductId.prevention:
        return true;
      case ProductId.glExtended:
        return (
          quotes.find((quote): boolean => quote.product.id === ProductId.glExtended)?.selected ??
          true
        );
      case ProductId.healthBasic:
        return healthUnlimited?.selected === false || !healthUnlimited;
      case ProductId.healthUnlimited:
        return healthBasic?.selected === false;
      default:
        throw new Error(`Unexpected product id: ${productId}`);
    }
  };

  private static getHealthProductsSelectedStatus(quotes: Quote[]): {
    healthBasic: Quote | undefined;
    healthUnlimited: Quote | undefined;
  } {
    const healthBasic = quotes.find((quote): boolean => quote.product.id === ProductId.healthBasic);
    const healthUnlimited = quotes.find(
      (quote): boolean => quote.product.id === ProductId.healthUnlimited
    );

    return { healthBasic, healthUnlimited };
  }

  get products(): Product[] {
    return this._products;
  }

  get coupon(): Coupon | undefined {
    return this._coupon;
  }

  get discount(): number {
    const basePrice =
      this._paymentIntervalMonths === PaymentIntervalMonths.yearly
        ? this.selectedProductsTotalPrice
        : this.selectedProductsMonthlyTotalPrice;

    return this.calculateDiscount(basePrice);
  }

  get currency(): string {
    return this._currency;
  }

  get selectedProducts(): Product[] {
    return this._products.filter((product) => product.selected);
  }

  get selectedProductsTotalPrice(): number {
    return this.calculateTotalPrice((product) => product.selected);
  }

  get selectedProductsDiscountedPrice(): number {
    return this.selectedProductsTotalPrice - this.discount;
  }

  get selectedProductsMonthlyTotalPrice(): number {
    return this.selectedProductsTotalPrice / ShoppingCart.MONTHS_IN_YEAR;
  }

  get selectedProductsMonthlyDiscountedPrice(): number {
    return this.selectedProductsMonthlyTotalPrice - this.discount;
  }

  get paymentIntervalMonths(): number {
    return this._paymentIntervalMonths;
  }

  get isNeededSelectHealthProduct(): boolean {
    return !this._products.some(
      (product) =>
        [ProductId.healthBasic, ProductId.healthUnlimited].includes(product.id) && product.selected
    );
  }

  get paymentAmount(): number {
    return Math.max(this.calculatePaymentAmount(), 0);
  }

  getPriceByHealthProduct(productId: ProductId): number {
    return this.calcPriceByHealthProduct(productId);
  }

  getProduct(productId: ProductId): Product | undefined {
    return this._products.find((product: Product): boolean => product.id === productId);
  }

  getHealthProducts(): Product[] {
    return this._products.filter(
      (product: Product) =>
        product.id === ProductId.healthBasic || product.id === ProductId.healthUnlimited
    );
  }

  getSelectedHealthProduct(): Product | undefined {
    return this.getHealthProducts().find((product: Product) => product.selected);
  }

  getMonthlyDiscountedPriceBreakdown(): Record<string, number>[] {
    if (!this._coupon) {
      return [];
    }

    const payments: Record<string, number>[] = [];
    const monthlyTotalPrice: number = this.selectedProductsMonthlyTotalPrice;
    const fullyCoveredMonths = Math.floor(this._coupon.amount_off / monthlyTotalPrice);
    const remainingDiscountAfterFullCover = this._coupon.amount_off % monthlyTotalPrice;

    for (let i = 0; i < fullyCoveredMonths; i++) {
      payments.push({ payment: 0, appliedDiscount: monthlyTotalPrice });
    }

    if (remainingDiscountAfterFullCover > 0) {
      const remainingPayment = monthlyTotalPrice - remainingDiscountAfterFullCover;
      payments.push({
        payment: remainingPayment,
        appliedDiscount: remainingDiscountAfterFullCover,
      });
    }

    return payments;
  }

  getMonthlyDiscountedPriceByHealthProduct(productId: ProductId): number {
    const priceByHealthProduct = this.calcPriceByHealthProduct(productId);

    return priceByHealthProduct === 0
      ? 0
      : priceByHealthProduct - this.calculateDiscount(priceByHealthProduct);
  }

  private findProductPrice(productId: ProductId): number {
    return this._products.find((product) => product.id === productId)?.price ?? 0;
  }

  private calcPriceByHealthProduct(productId: ProductId): number {
    const health = this.findProductPrice(productId);
    const prevention = this.findProductPrice(ProductId.prevention);
    const gl = this.getProduct(ProductId.glExtended)?.selected
      ? this.findProductPrice(ProductId.glExtended)
      : 0;

    if (health === 0) {
      return 0;
    }

    const bundlePrice = health + prevention + gl;

    return this.paymentIntervalMonths === PaymentIntervalMonths.yearly
      ? bundlePrice
      : bundlePrice / ShoppingCart.MONTHS_IN_YEAR;
  }

  private calculateTotalPrice(filter: (product: Product) => boolean): number {
    return this._products.reduce(
      (total, product) => total + (filter(product) ? product.price : 0),
      0
    );
  }

  private calculateDiscount(basePrice: number): number {
    if (!this._coupon) {
      return 0;
    }

    return this._coupon.type === PromotionCodeType.percentage
      ? basePrice * (this._coupon.percent_off / 100)
      : this._coupon.amount_off;
  }

  private calculatePaymentAmount(): number {
    const basePrice =
      this._paymentIntervalMonths === PaymentIntervalMonths.yearly
        ? this.selectedProductsTotalPrice
        : this.selectedProductsMonthlyTotalPrice;
    const discountedPrice =
      this._paymentIntervalMonths === PaymentIntervalMonths.yearly
        ? this.selectedProductsDiscountedPrice
        : this.selectedProductsMonthlyDiscountedPrice;

    return this._coupon ? discountedPrice : basePrice;
  }
}
